import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, of, throwError, catchError, tap, filter, take, map, switchMap, EMPTY, forkJoin } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { EntityType } from '../../objects/interface-library';
import { Coi } from '../../objects/coi';

@Injectable({
  providedIn: 'root'
})
export class CoiService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private allCoisSubject = new BehaviorSubject<any[]>([]);
  public readonly allCois$: Observable<any[]> = this.allCoisSubject.asObservable();

  private visibleCoisSubject = new BehaviorSubject<any[]>([]);
  public readonly visibleCois$: Observable<any[]> = this.visibleCoisSubject.asObservable();

  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  constructor() {
    // Auto-refresh COIs on service construction (like ContractService)
    this.refreshAllCois();
    this.setupVisibilityFiltering();
  }

  private setupVisibilityFiltering(): void {
    this.allCois$.subscribe(cois => {
      // For now, just pass through all COIs
      // TODO: Add permissions filtering when ready
      this.visibleCoisSubject.next(cois);
    });
  }

  /**
   * Refreshes all COIs by calling the paginated API and updating the subject.
   * Uses POST method with lastEvaluatedKey in the body (matching Lambda handler).
   */
  refreshAllCois(): void {
    const getPageOfCois = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.coiGetAllActiveUrl;
      const body = { lastEvaluatedKey };

      return this.http.post(url, body).pipe(
        // tap(response => console.log('Fetched COI page:', response)),
        map((response: any) => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('Error fetching COI page:', error);
          // Return an object that our pagination service can understand to stop.
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
    };

    // Use the PaginationService to handle the recursive fetching of all pages.
    this.paginationService.getAllPaginatedDataProgressively(getPageOfCois)
      .pipe(
        // tap(allCois => console.log('Successfully fetched all COIs:', allCois)),
        catchError(error => {
          console.error('Error fetching all COIs:', error);
          this.allCoisSubject.next([]);
          return of([]);
        })
      )
      .subscribe(allCois => {
        // Since `getAllPaginatedData` returns a flattened array of all items,
        // we can directly update our subject.
        // Handle both response.coiData and direct COI objects
        const filteredObjects = allCois.map((obj: any) => obj.coiData || obj);
        this.allCoisSubject.next(filteredObjects);
      });
  }


  /**
   * Gets a fully-hydrated COI object by its ID.
   * @param coiId The ID of the COI to retrieve.
   * @returns An observable of the fully-hydrated COI or null if not found.
   */
  public getCoiObjectFullyHydrated(coiId: string): Observable<any | null> {
    // For now, just return the COI object without hydration
    // TODO: Add full hydration when ConnectionsService is ready
    return this.getCoiObject(coiId).pipe(
      catchError(error => {
        console.error('[CoiService.getCoiObjectFullyHydrated] Error:', error);
        return of(null);
      })
    );
  }


  /**
   * Gets a specific COI from the in-memory cache by its ID.
   * This is what ConnectionsService will use to get a fully populated object.
   * @param coiId The ID of the COI to retrieve.
   * @returns An observable of the found COI or null if not found.
   */
  public getCoiObject(coiId: string): Observable<any | null> {
    return this.allCois$.pipe(
      map(cois => {
        const found = cois.find(coi => coi.coiId === coiId || coi.coiNum === coiId);
        if (!found) {
          console.warn('[CoiService:Diag] getCoiObject - not found for:', coiId, '| Cache size:', cois.length);
        }
        return found || null;
      }),
      take(1)
    );
  }

  /**
   * Gets a specific COI from the in-memory cache by its coiNum.
   * @param coiNum The coiNum of the COI to retrieve.
   * @returns An observable of the found COI or null if not found.
   */
  public getCoiByNumber(coiNum: string): Observable<any | null> {
    return this.allCois$.pipe(
      map(cois => {
        const foundCoi = cois.find(coi => coi.coiNum === coiNum);
        if (foundCoi) {
          return foundCoi;
        } else {
          console.warn('CoiService | getCoiByNumber | Did not find COI');
          return null;
        }
      }),
      take(1)
    );
  }


  /**
   * Writes a COI (Certificate of Insurance) object to the database.
   * This is a placeholder function using a PUT request.
   * @param coi The COI object to write.
   * @returns An observable of the API response.
   */
  public writeCoi(coi: Coi): Observable<any> {
    const options = { headers: this.headers };
    return this.http.put(`${environment.coiWriteUrl}`, coi, options)
      .pipe(
        switchMap(response => {
          return this.refreshAllCoisAsync().pipe(
            map(() => {
              return response;
            })
          );
        }),
        catchError(error => {
          console.error('An error occurred while writing the COI:', error);
          return throwError(() => new Error('Could not write COI.'));
        })
      );
  }

  /**
   * Refreshes all COIs and returns an Observable that completes when the refresh is done.
   * This is useful when you need to wait for the refresh before performing another action.
   */
  private refreshAllCoisAsync(): Observable<any[]> {
    const getPageOfCois = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.coiGetAllActiveUrl;
      const body = { lastEvaluatedKey };

      return this.http.post(url, body).pipe(
        map((response: any) => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('Error fetching COI page in refreshAllCoisAsync:', error);
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
    };

    return this.paginationService.getAllPaginatedDataProgressively(getPageOfCois)
      .pipe(
        map(allCois => {
          const filteredObjects = allCois.map((obj: any) => obj.coiData || obj);
          // console.log('[CoiService.refreshAllCoisAsync] Updating subject with', filteredObjects.length, 'COIs');
          this.allCoisSubject.next(filteredObjects);
          return filteredObjects;
        }),
        catchError(error => {
          console.error('Error in refreshAllCoisAsync:', error);
          this.allCoisSubject.next([]);
          return of([]);
        })
      );
  }

  /**
   * Updates a single attribute of a COI.
   * Endpoint: PUT /api/{org}/update-coi-attribute
   * Body: { coiNum: string, attribute: string, value: any }
   *
   * @param coiNum The COI number/ID to update.
   * @param attribute The attribute name to update.
   * @param attributeValue The new value for the attribute.
   * @returns An observable of the API response.
   */
  public updateCoiAttribute(coiNum: any, attribute: any, attributeValue: any): Observable<any> {
    // Construct URL from the coiWriteUrl base (removes '/write-coi' and adds the new endpoint)
    const URL = environment.URL_COI_UPDATE_ATTRIBUTE;
    const body = { coiNum, attribute, value: attributeValue };
    return this.http.put(URL, body).pipe(
      tap(() => this.refreshAllCois()), // Refresh cache after update
      catchError(error => {
        console.error('Error updating COI attribute:', error);
        return throwError(() => new Error('Could not update COI attribute.'));
      })
    );
  }

}
